// Packages
import React, { useState, useEffect, useMemo } from "react";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { useTheme } from "@mui/material/styles";
import { cloneDeep, isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";

// Components
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ModalContainer from "components/ModalContainer";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Sub Components
import SchedulePicker from "./SchedulePicker";
import PendingChildren from '../PendingChildren';

// Img
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

// Const
import { getUpdateModalColumn } from "../../const/columns";

// Helper
import {
  getActionSelectorSendRequestParams,
  initUpdateActionFw,
  getIsInvalid,
  getUpdateSelectorCleanSavingPayload
} from "../../helper";

const UpdateModal = ({
  isError,
  setIsError,
  messages,
  setMessages,
  mainTableInstance,
  actions,
  setActions,
  openedAction,
  selectedFWInstancesList,
  initCallback
}) => {
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth } = useAuthScope();

  const [uuids] = useState(() => ({
    actionSelector: {
      updateNow: () => uuidv4(),
      scheduleUpdate: () => uuidv4(),
      cancelUpdate: () => uuidv4(),
    },
  }));
  const uuid = useMemo(() => uuids.actionSelector[openedAction.key] && uuids.actionSelector[openedAction.key](), [openedAction.key]);

  const [selectedFWList, setSelectedFWList] = useState([]);
  const demoFW = selectedFWList?.[0];
  const isLengthThanOne = !!demoFW && selectedFWList?.length > 1;

  const [isNext, setIsNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isInvalid = useMemo(() => getIsInvalid({
    selectedFWList,
    openedAction
  }), [selectedFWList, openedAction?.key]);

  const handleTargetFwVers = changedTargetFw => {
    const clonedList = selectedFWList.map(fw => {
      const isSerialNameSame = fw.seriesName === changedTargetFw.seriesName;
      const isHwVersionSame = fw.hwVersion === changedTargetFw.hwVersion;
      return isSerialNameSame && isHwVersionSame ? changedTargetFw : fw;
    })

    setSelectedFWList(cloneDeep(clonedList));
  }

  const handleCloseModal = () => {
    setActions({
      ...cloneDeep(actions),
      [openedAction.key]: {
        ...cloneDeep(openedAction),
        isOpenModal: false
      }
    });
  }

  const handleSubmit = () => {
    const payload = getUpdateSelectorCleanSavingPayload({
      selectedFWList,
      openedAction
    });

    const { apiKey, restfulParams } = getActionSelectorSendRequestParams({ auth });

    sendRequest(
      apiKey,
      uuid,
      restfulParams,
      payload
    );
  }

  const handleCancelSubmit = () => {
    const payload = getUpdateSelectorCleanSavingPayload({
      selectedFWList,
      openedAction
    });

    setIsLoading(true);

    const { apiKey, restfulParams } = getActionSelectorSendRequestParams({ auth });

    sendRequest(
      apiKey,
      uuid,
      restfulParams,
      payload
    );

    setIsLoading(false);
  }

  const handleResetModal = () => {
    setIsLoading(false);
  }

  const updateTableInstance = useTableInstance({
    tableName: "firmwareUpdateModalTable",
    data: selectedFWList,
    enablePagination: true,
    columns: getUpdateModalColumn({
      openedAction,
      columnHelper,
      theme,
      callbacks: {
        handleTargetFwVers
      },
      isNext
    }),
  });

  useEffect(() => {
    if (selectedFWInstancesList) {
      setIsLoading(true);

      const fwList = initUpdateActionFw({
        selectedFWInstancesList
      });
      setSelectedFWList(fwList);
    }

    return () => {
      handleResetModal();
    }
  }, [])

  useEffect(() => {
    return () => {
      mainTableInstance.setRowSelection({});
      setIsNext(false);
    }
  }, []);

  useEffect(() => {
    const response = getData(uuid);

    const isRespError = !!response?.error || response?.code >= 400;
    const isRespSuccess = !!response?.data || !!response?.result;

    if (isRespError) {
      setIsLoading(false);
      setIsError(true);
      setMessages({
        ...messages,
        error: response?.message || ""
      });

      return;
    }

    if (isRespSuccess) {
      if (openedAction.key === "cancelUpdate") {
        setIsNext(true);
        setSelectedFWList([]);
      }else{
        handleCloseModal();
      }

      setIsLoading(false);
      initCallback();

    }

  }, [dataMap])

  return (
    <ModalContainer display={!!openedAction} modalWidth={"1250px"}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}>
          {openedAction?.label?.modal || "-"}
        </Typography>

        <Button
          onClick={handleCloseModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}>
          <CloseIcon />
        </Button>

      </Box>

      <Box sx={{ marginBottom: "20px" }}>
        <Box>
          <Typography sx={{
            color: theme.palette.primary.black,
            fontSize: theme.typography.customFontSize[2],
          }}>
            {openedAction.description.info}
          </Typography>
        </Box>
      </Box>
      {
        openedAction.key === "scheduleUpdate" &&
        <SchedulePicker
          {...{
            selectedFWList,
            setSelectedFWList,
            demoFW,
            isLengthThanOne,
            isNext
          }}
        />
      }

      {
        (selectedFWInstancesList?.length === 0 && <CircularProgress />) ||
        <>
          <Box>
            <TableField
              {...updateTableInstance}
              pendingChildren={
                <PendingChildren
                  {...{
                    isLoading: false,
                    list: selectedFWList,
                    // description: openedAction.description.noData
                  }}
                />
              }
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "50px" }}>

            {/* Cancel / Back Button */}
            <>
              {
                !isNext &&
                <Box margin="0 10px">
                  <OutlineButton
                    width="110px"
                    height="32px"
                    onClick={() => {
                      setIsNext(false);
                      handleCloseModal();
                    }}
                  >
                    Cancel
                  </OutlineButton>
                </Box>
              }

              {
                isNext &&
                <Box margin="0 10px">
                  <OutlineButton
                    width="110px"
                    height="32px"
                    onClick={() => {
                      if (openedAction.key === "cancelUpdate") {
                        handleCloseModal();
                      } else {
                        setIsNext(false);
                      }
                    }}
                  >
                    {openedAction.label.prevButton}
                  </OutlineButton>
                </Box>
              }
            </>

            {/* Next / Apply Button */}
            <>
              {
                !isNext &&
                <Box margin="0 10px">
                  <SolidButton
                    width="110px"
                    height="32px"
                    disabled={isInvalid}
                    onClick={() => {
                      if (openedAction.key === "cancelUpdate") {
                        handleCancelSubmit();
                      } else {
                        setIsNext(true);
                      }
                    }}
                  >
                    {openedAction.label.nextButton}
                  </SolidButton>
                </Box>
              }

              {
                isNext &&
                <Box margin="0 10px">
                  <SolidButton
                    width="110px"
                    height="32px"
                    disabled={isInvalid}
                    onClick={() => {
                      if (openedAction.key === "cancelUpdate") {
                        handleCloseModal();
                      } else {
                        handleSubmit();
                      }
                    }}
                  >
                    {openedAction.label.submitButton}
                  </SolidButton>
                </Box>
              }
            </>

          </Box>
        </>
      }
    </ModalContainer>
  );
};

export default UpdateModal;